import { FormEvent, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ColumnDef } from '@tanstack/react-table';
import { useNavigate } from 'react-router-dom';
import { api, ApiClientError } from '../lib/api';
import { Badge, Button, Input, Select } from '../atoms';
import { PageHeader } from '../shell';
import { DataTable, Pagination, Modal, ListToolbar, useToast } from '../organisms';
import { useServerList } from '../lib/useServerList';
import { useEntityPermission } from '../lib/usePermissions';
import { formatDate } from '../lib/orgTimezone';

/**
 * TransmittalsList — /v2/transmittals
 *
 * The tax-bill transmittal as a first-class tracked entity (Domain 15 · PRD 15.3,
 * backlog 15.3). Lists the org's transmittals over the REAL backend
 * (GET /api/transmittals?q&status&sort&dir&page&limit — src/routes/transmittals.js →
 * services/transmittals) and opens the create flow (POST /api/transmittals) which
 * drafts a transmittal over a selection of tax bills, then routes to the detail
 * view to generate + send.
 *
 * Server-side list (PRD 06.1): the debounced search box (subject / recipient),
 * the status filter, the sortable column headers, and the pager all drive the
 * server — the canonical useServerList + ListToolbar + DataTable pattern shared
 * with ParcelsList / ReturnsList. No client-side filtering/slicing.
 *
 * Row-scope + the org/client transmit gate are enforced server-side: a non-booked
 * caller sees an empty list; a non-transmitting org/client gets a 403 on create,
 * surfaced here as a toast (the same actions the FE would otherwise hide).
 */

export type TransmittalStatus = 'draft' | 'generated' | 'sent' | 'delivered' | 'failed';

export interface Transmittal {
  id: string;
  organization_id: string;
  client_id: string | null;
  status: TransmittalStatus;
  template_id: string | null;
  artifact_format: string | null;
  r2_key: string | null;
  recipient_kind: 'client' | 'collector' | 'other' | null;
  recipient_email: string | null;
  subject: string | null;
  email_outbox_id: string | null;
  failed_reason: string | null;
  generated_at: string | null;
  sent_at: string | null;
  delivered_at: string | null;
  created_by_user_id: string | null;
  created_at: string;
  updated_at: string;
  bill_count?: number;
}

export const STATUS_VARIANT: Record<TransmittalStatus, 'success' | 'warning' | 'neutral' | 'info' | 'danger'> = {
  draft: 'neutral',
  generated: 'info',
  sent: 'warning',
  delivered: 'success',
  failed: 'danger',
};

function fmtDate(v: string | null | undefined): string {
  return formatDate(v);
}

const STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'generated', label: 'Generated' },
  { value: 'sent', label: 'Sent' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'failed', label: 'Failed' },
];

export function TransmittalsList() {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  // Creating a transmittal drafts over tax bills → gate on tax_bill.can_add.
  const canAddTransmittal = useEntityPermission('tax_bill', 'can_add');

  const list = useServerList<Transmittal>({
    resource: 'transmittals',
    queryKey: ['transmittals-list'],
    defaultPageSize: 25,
    defaultSort: { id: 'created_at', desc: true },
    filters: { status: statusFilter },
  });

  const columns = useMemo<ColumnDef<Transmittal>[]>(() => [
    {
      accessorKey: 'created_at',
      header: 'Created',
      cell: ({ getValue }) => fmtDate(getValue() as string),
    },
    {
      accessorKey: 'subject',
      header: 'Subject',
      cell: ({ getValue }) => (getValue() as string | null) || <span style={{ color: 'var(--color-muted)' }}>(no subject)</span>,
    },
    {
      accessorKey: 'recipient_email',
      header: 'Recipient',
      cell: ({ row }) => {
        const t = row.original;
        return t.recipient_email
          ? <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)' }}>{t.recipient_email}</span>
          : <Badge variant="neutral">{t.recipient_kind || 'client'}</Badge>;
      },
    },
    {
      id: 'bills',
      header: 'Bills',
      cell: ({ row }) => row.original.bill_count ?? '—',
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ getValue }) => {
        const s = getValue() as TransmittalStatus;
        return <Badge variant={STATUS_VARIANT[s]} dot>{s}</Badge>;
      },
    },
  ], []);

  const { query, rows, total } = list;
  const hasFilters = list.search !== '' || statusFilter !== '';

  return (
    <>
      <PageHeader
        title="Transmittals"
        subtitle={query.isLoading ? 'Loading…' : `${total.toLocaleString()} transmittal${total === 1 ? '' : 's'}`}
        actions={
          canAddTransmittal ? (
            <Button variant="primary" size="md" onClick={() => setCreateOpen(true)}>
              + New transmittal
            </Button>
          ) : null
        }
      />

      <ListToolbar
        searchValue={list.searchInput}
        onSearchChange={list.setSearchInput}
        searchPlaceholder="Search transmittals by subject or recipient…"
        filters={[{
          key: 'status',
          value: statusFilter,
          onChange: setStatusFilter,
          placeholder: 'All statuses',
          options: STATUS_OPTIONS,
        }]}
      />

      {query.error && (
        <div style={{
          padding: 'var(--sp-4)',
          background: 'var(--color-status-red-bg)',
          border: '1px solid var(--color-status-red)',
          color: 'var(--color-status-red)',
          borderRadius: 'var(--r-lg)',
          marginBottom: 'var(--sp-4)',
        }}>
          <strong>Failed to load transmittals:</strong> {(query.error as Error).message}
        </div>
      )}

      {!query.isLoading && total === 0 && !query.error && (
        <div style={{
          padding: hasFilters ? 'var(--sp-7)' : 'var(--sp-8)',
          background: 'var(--color-card)',
          border: '1px dashed var(--color-border)',
          borderRadius: 'var(--r-lg)',
          color: 'var(--color-muted)',
          textAlign: 'center',
        }}>
          {hasFilters
            ? <p style={{ margin: 0, fontSize: 'var(--fs-md)' }}>No transmittals match your search or filters.</p>
            : (
              <>
                <h2 style={{
                  margin: '0 0 var(--sp-2)',
                  fontSize: 'var(--fs-xl)',
                  fontWeight: 'var(--fw-semibold)',
                  color: 'var(--color-ink-3)',
                }}>No transmittals yet</h2>
                <p style={{ margin: 0, fontSize: 'var(--fs-md)' }}>
                  Click "+ New transmittal" to draft a branded cover document over one or more tax bills.
                </p>
              </>
            )}
        </div>
      )}

      {(total > 0 || query.isFetching) && !query.error && (
        <>
          <DataTable
            data={rows}
            columns={columns}
            sorting={list.sorting}
            onSortingChange={list.setSorting}
            onRowClick={(row) => navigate(`/transmittals/${row.id}`)}
            emptyMessage={query.isFetching ? 'Loading…' : 'No transmittals'}
          />
          <Pagination
            pageIndex={list.pageIndex}
            pageCount={list.pageCount}
            pageSize={list.pageSize}
            totalRows={total}
            onPageChange={list.setPageIndex}
            onPageSizeChange={list.setPageSize}
          />
        </>
      )}

      <CreateTransmittalModal open={createOpen} onClose={() => setCreateOpen(false)} />
    </>
  );
}

// ── Create flow ─────────────────────────────────────────────

interface TaxBillLite {
  id: string;
  account_number: string | null;
  tax_year: number | null;
  status: string | null;
  tax_amount: number | null;
}

function errMessage(err: unknown): string {
  return err instanceof ApiClientError && err.body && typeof err.body === 'object' && 'error' in err.body
    ? String((err.body as { error: unknown }).error)
    : err instanceof Error ? err.message : String(err);
}

const RECIPIENT_KINDS = [
  { value: 'client', label: 'Client' },
  { value: 'collector', label: 'Collector' },
  { value: 'other', label: 'Other' },
];

/**
 * Draft a transmittal over a selection of tax bills. POST /api/transmittals with
 * { tax_bill_ids, recipient_kind, recipient_email?, subject? }. On success routes
 * to the new transmittal's detail view where it can be generated + sent. The tax
 * bill picker reads the REAL, row-scoped GET /api/tax-bills list.
 */
export function CreateTransmittalModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [billSearch, setBillSearch] = useState('');
  const [recipientKind, setRecipientKind] = useState('client');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [notes, setNotes] = useState('');
  // The create action POSTs /api/transmittals over tax bills → tax_bill.can_add.
  const canAdd = useEntityPermission('tax_bill', 'can_add');

  const billsQuery = useQuery<TaxBillLite[]>({
    queryKey: ['tax-bills-for-transmittal'],
    queryFn: ({ signal }) => api.get<TaxBillLite[]>('/api/tax-bills', signal),
    enabled: open,
  });

  function reset() {
    setSelected(new Set());
    setBillSearch('');
    setRecipientKind('client');
    setRecipientEmail('');
    setSubject('');
    setReferenceNumber('');
    setNotes('');
  }

  const mutation = useMutation<Transmittal, Error, void>({
    mutationFn: () => api.post<Transmittal>('/api/transmittals', {
      tax_bill_ids: [...selected],
      recipient_kind: recipientKind,
      recipient_email: recipientEmail.trim() || null,
      subject: subject.trim() || null,
      reference_number: referenceNumber.trim() || null,
      notes: notes.trim() || null,
    }),
    onSuccess: (created) => {
      toast({ variant: 'success', message: 'Draft transmittal created.' });
      queryClient.invalidateQueries({ queryKey: ['transmittals-list'] });
      reset();
      onClose();
      navigate(`/transmittals/${created.id}`);
    },
    onError: (err) => toast({ variant: 'error', message: errMessage(err) }),
  });

  const bills = billsQuery.data ?? [];
  const filteredBills = useMemo(() => {
    const q = billSearch.trim().toLowerCase();
    if (!q) return bills;
    return bills.filter((b) =>
      (b.account_number || '').toLowerCase().includes(q)
      || String(b.tax_year || '').includes(q));
  }, [bills, billSearch]);

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  const canSubmit = selected.size > 0 && !mutation.isPending;

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (canSubmit) mutation.mutate();
  }

  return (
    <Modal
      open={open}
      onClose={() => { if (!mutation.isPending) { reset(); onClose(); } }}
      title="New transmittal"
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={() => { reset(); onClose(); }} disabled={mutation.isPending}>Cancel</Button>
          {canAdd && (
            <Button variant="primary" loading={mutation.isPending} disabled={!canSubmit} onClick={() => canSubmit && mutation.mutate()}>
              Create draft
            </Button>
          )}
        </>
      }
    >
      <form onSubmit={handleSubmit}>
        <FieldLabel text={`Tax bills${selected.size ? ` · ${selected.size} selected` : ''}`} />
        <div style={{ marginBottom: 'var(--sp-2)' }}>
          <Input
            type="search"
            value={billSearch}
            onChange={(e) => setBillSearch(e.target.value)}
            placeholder="Filter by account # or year…"
            aria-label="Filter tax bills"
          />
        </div>
        <div style={{
          maxHeight: 240,
          overflowY: 'auto',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--r-md)',
          marginBottom: 'var(--sp-4)',
        }}>
          {billsQuery.isLoading && <p style={{ padding: 'var(--sp-3)', margin: 0, color: 'var(--color-ink-3)' }}>Loading bills…</p>}
          {billsQuery.error && <p style={{ padding: 'var(--sp-3)', margin: 0, color: 'var(--color-status-red)' }}>Failed to load bills: {(billsQuery.error as Error).message}</p>}
          {!billsQuery.isLoading && filteredBills.length === 0 && (
            <p style={{ padding: 'var(--sp-3)', margin: 0, color: 'var(--color-muted)' }}>No tax bills found.</p>
          )}
          {filteredBills.map((b) => {
            const checked = selected.has(b.id);
            return (
              <label
                key={b.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--sp-3)',
                  padding: 'var(--sp-2) var(--sp-3)',
                  borderBottom: '1px solid var(--color-border)',
                  cursor: 'pointer',
                  background: checked ? 'var(--brand-primary-bg)' : 'transparent',
                  fontSize: 'var(--fs-sm)',
                }}
              >
                <input type="checkbox" checked={checked} onChange={() => toggle(b.id)} aria-label={`Select bill ${b.account_number || b.id}`} />
                <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 'var(--fw-medium)' }}>{b.account_number || b.id.slice(0, 8)}</span>
                <span style={{ color: 'var(--color-ink-3)' }}>{b.tax_year ?? '—'}</span>
                {b.status && <Badge variant="neutral">{b.status.replace(/_/g, ' ')}</Badge>}
                <span style={{ marginLeft: 'auto', color: 'var(--color-ink-3)' }}>
                  {b.tax_amount != null ? `$${b.tax_amount.toLocaleString()}` : ''}
                </span>
              </label>
            );
          })}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--sp-3)', marginBottom: 'var(--sp-3)' }}>
          <label style={{ display: 'block' }}>
            <FieldLabel text="Recipient kind" />
            <Select
              options={RECIPIENT_KINDS}
              value={recipientKind}
              onChange={(e) => setRecipientKind(e.target.value)}
              aria-label="Recipient kind"
            />
          </label>
          <label style={{ display: 'block' }}>
            <FieldLabel text="Recipient email (optional)" />
            <Input
              type="email"
              value={recipientEmail}
              onChange={(e) => setRecipientEmail(e.target.value)}
              placeholder="owner@example.com"
            />
          </label>
        </div>
        <label style={{ display: 'block' }}>
          <FieldLabel text="Subject (optional)" />
          <Input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Your 2026 tax bills"
          />
        </label>
        <label style={{ display: 'block' }}>
          <FieldLabel text="Remittance reference (optional)" />
          <Input
            type="text"
            value={referenceNumber}
            onChange={(e) => setReferenceNumber(e.target.value)}
            placeholder="Batch / remittance #"
          />
        </label>
        <label style={{ display: 'block' }}>
          <FieldLabel text="Cover-page notes (optional)" />
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            placeholder="Shown in the cover-page footer (e.g. payment instructions)."
            style={{
              width: '100%', padding: 'var(--sp-2)', border: '1px solid var(--color-border)',
              borderRadius: 'var(--r-md)', font: 'inherit', color: 'var(--color-ink)',
              background: 'var(--color-card)', resize: 'vertical', boxSizing: 'border-box',
            }}
          />
        </label>
        <p style={{ margin: 'var(--sp-3) 0 0', fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>
          Leave the email blank to use the client's invoicing/primary contact at send time.
          The reference + notes appear on the transmittal cover page.
        </p>
      </form>
    </Modal>
  );
}

function FieldLabel({ text }: { text: string }) {
  return (
    <span style={{
      display: 'block',
      marginBottom: 'var(--sp-1)',
      fontSize: 'var(--fs-sm)',
      fontWeight: 'var(--fw-medium)',
      color: 'var(--color-ink)',
    }}>{text}</span>
  );
}
